"use client";

import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Container } from "@/components/ui/Container";
import { HeaderNav } from "@/components/HeaderNav";
import { Footer } from "@/components/Footer";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageProvider } from "@/contexts/LanguageContext";

const translations = {
  en: {
    backToHome: "Back to home",
    title: "Terms &",
    titleHighlight: "Conditions",
    sections: [
      {
        title: "1. Acceptance of Terms",
        content: "By accessing and using this website, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to abide by these terms, please do not use this site."
      },
      {
        title: "2. Services Description",
        content: "Azurys provides web design and development services. The specific terms of each project will be detailed in individual proposals and contracts. All project timelines, deliverables, and pricing are subject to negotiation and mutual agreement."
      },
      {
        title: "3. Payment Terms",
        content: "Payment terms will be specified in individual project proposals. Generally, a deposit is required before work begins, with the remaining balance due upon project completion. Late payments may incur additional fees."
      },
      {
        title: "4. Intellectual Property",
        content: "Upon full payment, clients receive ownership of the final deliverables. Azurys retains the right to display completed work in portfolios unless otherwise agreed. All source code libraries and frameworks remain the property of their respective owners."
      },
      {
        title: "5. Revisions and Changes",
        content: "Each project includes a specified number of revision rounds. Additional revisions beyond the agreed scope may incur extra charges. Major changes to project scope after approval may require a new proposal."
      },
      {
        title: "6. Project Timeline",
        content: "While we strive to meet all deadlines, timelines may be affected by client feedback delays, scope changes, or unforeseen circumstances. We will communicate any expected delays promptly."
      },
      {
        title: "7. Limitation of Liability",
        content: "Azurys shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services. Our total liability shall not exceed the amount paid for the specific service."
      },
      {
        title: "8. Termination",
        content: "Either party may terminate a project with written notice. In case of termination, the client is responsible for payment of all work completed to date. Deposits are non-refundable."
      },
      {
        title: "9. Modifications",
        content: "Azurys reserves the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the new terms."
      }
    ]
  },
  fr: {
    backToHome: "Retour à l'accueil",
    title: "Conditions",
    titleHighlight: "Générales",
    sections: [
      {
        title: "1. Acceptation des Conditions",
        content: "En accédant et en utilisant ce site web, vous acceptez d'être lié par les termes et dispositions de cet accord. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser ce site."
      },
      {
        title: "2. Description des Services",
        content: "Azurys fournit des services de conception et de développement web. Les conditions spécifiques de chaque projet seront détaillées dans les propositions et contrats individuels. Tous les délais, livrables et tarifs sont sujets à négociation et accord mutuel."
      },
      {
        title: "3. Conditions de Paiement",
        content: "Les conditions de paiement seront spécifiées dans les propositions de projet individuelles. Généralement, un acompte est requis avant le début des travaux, le solde restant étant dû à la fin du projet. Les retards de paiement peuvent entraîner des frais supplémentaires."
      },
      {
        title: "4. Propriété Intellectuelle",
        content: "Après paiement intégral, les clients reçoivent la propriété des livrables finaux. Azurys conserve le droit d'afficher les travaux achevés dans ses portfolios sauf accord contraire. Toutes les bibliothèques de code source et frameworks restent la propriété de leurs propriétaires respectifs."
      },
      {
        title: "5. Révisions et Modifications",
        content: "Chaque projet comprend un nombre spécifié de cycles de révision. Les révisions supplémentaires au-delà du périmètre convenu peuvent entraîner des frais supplémentaires. Les changements majeurs de périmètre après approbation peuvent nécessiter une nouvelle proposition."
      },
      {
        title: "6. Délais de Projet",
        content: "Bien que nous nous efforcions de respecter tous les délais, ceux-ci peuvent être affectés par les retards de retour client, les changements de périmètre ou des circonstances imprévues. Nous communiquerons rapidement tout retard prévu."
      },
      {
        title: "7. Limitation de Responsabilité",
        content: "Azurys ne sera pas responsable des dommages indirects, accessoires, spéciaux ou consécutifs découlant de l'utilisation de nos services. Notre responsabilité totale ne dépassera pas le montant payé pour le service spécifique."
      },
      {
        title: "8. Résiliation",
        content: "Chaque partie peut résilier un projet avec un préavis écrit. En cas de résiliation, le client est responsable du paiement de tous les travaux réalisés à ce jour. Les acomptes ne sont pas remboursables."
      },
      {
        title: "9. Modifications",
        content: "Azurys se réserve le droit de modifier ces conditions à tout moment. L'utilisation continue de nos services après les modifications constitue l'acceptation des nouvelles conditions."
      }
    ]
  }
};

const seoContent = {
  fr: {
    title: "Conditions Générales - Azurys | Agence web Paris",
    description: "Conditions générales d'utilisation et de vente d'Azurys. Informations sur les services, paiements, propriété intellectuelle et responsabilités."
  },
  en: {
    title: "Terms & Conditions - Azurys | Web Agency Paris",
    description: "Azurys terms and conditions. Information about services, payments, intellectual property and liabilities."
  }
};

function TermsContent() {
  const { lang } = useLanguage();
  const t = translations[lang];
  const seo = seoContent[lang];

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href="https://www.azurys.fr/terms" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <HeaderNav />
        
        <main className="pt-32 pb-20">
          <Container className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
              >
                <ArrowLeft size={16} />
                {t.backToHome}
              </Link>

              <h1 className="text-4xl sm:text-5xl font-bold mb-8">
                {t.title} <span className="gradient-text">{t.titleHighlight}</span>
              </h1>

              <div className="prose prose-invert max-w-none space-y-8">
                {t.sections.map((section, index) => (
                  <motion.section
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="p-6 rounded-2xl border border-white/[0.08] bg-card/40 backdrop-blur-sm"
                  >
                    <h2 className="text-2xl font-bold text-foreground mb-4">{section.title}</h2>
                    <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                  </motion.section>
                ))}
              </div>
            </motion.div>
          </Container>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default function Terms() {
  return (
    <LanguageProvider>
      <TermsContent />
    </LanguageProvider>
  );
}
